import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

type Props = {
	emptyState?: React.ReactNode;
	head: {
		cells: {
			content: React.ReactNode;
			key: string;
			width?: string;
		}[];
	};
	rows: {
		cells: {
			content: React.ReactNode;
		}[];
		key: string;
	}[];
};

const styles = cssMap({
	tableContainer: {
		width: '100%',
		tableLayout: 'fixed',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.300'),
		marginLeft: token('space.0'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		borderCollapse: 'separate',
		borderSpacing: 0,
		overflow: 'hidden',
	},
	tableHeader: {
		verticalAlign: 'middle',
		inset: token('space.0'),
		position: 'relative',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderTopLeftRadius: token('radius.large'),
	},
	tableHeaderCell: {
		display: 'table-cell',
		position: 'relative',
		appearance: 'none',
		verticalAlign: 'middle',
		borderStyle: 'none',
		boxSizing: 'border-box',
		borderBottomStyle: 'solid',
		borderBottomWidth: token('border.width'),
		borderBottomColor: token('color.border'),
		font: token('font.heading.xxsmall'),
		color: token('color.text.subtle'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		// @ts-ignore -next-line - override css reset rules
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:nth-of-type(1)': {
			paddingLeft: token('space.100'),
		},
	},
	cell: {
		display: 'table-cell',
		position: 'relative',
		appearance: 'none',
		verticalAlign: 'middle',
		borderImage: 'initial',
		borderStyle: 'none',
		boxSizing: 'border-box',
		borderBottomStyle: 'solid',
		borderBottomWidth: token('border.width'),
		borderBottomColor: token('color.border'),
		font: token('font.body'),
		color: token('color.text'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		// @ts-ignore -next-line - override css reset rules
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:nth-of-type(1)': {
			paddingLeft: token('space.100'),
		},
	},
});

export const Table = ({ head, rows, emptyState }: Props): React.JSX.Element => (
	<Box as="table" xcss={styles.tableContainer}>
		<Box as="thead" xcss={styles.tableHeader}>
			<Box as="tr">
				{head.cells.map((cell) => (
					<Box as="td" xcss={styles.tableHeaderCell} width={cell.width} key={cell.key}>
						{cell.content}
					</Box>
				))}
			</Box>
		</Box>
		<tbody>
			{rows.length > 0 ? (
				rows.map((row) => (
					<Box as="tr" key={row.key}>
						{row.cells.map((cell, index: number) => (
							<Box as="td" xcss={styles.cell} key={`${row.key}-${index}`}>
								{cell.content}
							</Box>
						))}
					</Box>
				))
			) : (
				<tr>
					<td colSpan={head.cells.length}>{emptyState}</td>
				</tr>
			)}
		</tbody>
	</Box>
);
